import { TableFunction, } from "./function-registry";
import { MISSING_VALUE, } from "./type-registry";
import { SCALAR_UNIT, TIMESTAMP_UNIT_SECONDS } from "../unit-system";
import { textEquals } from "./text-equals";
// ---------------------------------------------------------------------------
// Shared row helpers
//
// The evaluator-native table is keyed by COLUMN CODE: each schema column's
// `name` holds the DB column code, and `row.data` is keyed by code. So the same
// identifier the user types (`measurements.temp`, `last(measurements).temp`,
// or a bare `temp` inside a predicate) resolves consistently here.
// ---------------------------------------------------------------------------
function isEvalError(value) {
    return (typeof value === "object" &&
        value !== null &&
        "message" in value &&
        !("value" in value));
}
function cellToEvalValue(cellValue, column) {
    if (column.type === "number") {
        return {
            type: "scalar",
            value: typeof cellValue === "number" ? cellValue : parseFloat(String(cellValue)),
            unit: column.unit || SCALAR_UNIT,
        };
    }
    if (column.type === "datetime") {
        return {
            type: "scalar",
            value: new Date(cellValue).getTime() / 1000,
            unit: TIMESTAMP_UNIT_SECONDS,
        };
    }
    // Text cells keep their text: comparable with == / != ("TC" == organism),
    // refused anywhere a number is expected.
    return { type: "text", value: String(cellValue) };
}
function indexToEvalValue(index, indexType) {
    if (indexType === "timestamp") {
        return {
            type: "scalar",
            value: new Date(index).getTime() / 1000,
            unit: TIMESTAMP_UNIT_SECONDS,
        };
    }
    if (indexType === "string") {
        return { type: "text", value: String(index) };
    }
    if (typeof index === "number") {
        return { type: "scalar", value: index, unit: SCALAR_UNIT };
    }
    return { type: "scalar", value: parseFloat(String(index)) || 0, unit: SCALAR_UNIT };
}
/** Row → object record (omits null cells), used for the object return value. */
function rowToRecord(row, tableValue) {
    const rec = {};
    if (row.index !== undefined && row.index !== null && tableValue.indexColumn) {
        const axis = indexToEvalValue(row.index, tableValue.schema.indexColumn?.type ?? "number");
        rec._index = axis;
        // The axis is also readable by its own column name, like any other cell.
        rec[tableValue.indexColumn] = axis;
    }
    for (const column of tableValue.schema.columns) {
        const cell = row.data[column.name];
        if (cell !== null && cell !== undefined) {
            rec[column.name] = cellToEvalValue(cell, column);
        }
    }
    return rec;
}
function buildRowObject(row, tableValue) {
    return { type: "object", value: rowToRecord(row, tableValue) };
}
/**
 * Per-row scope for predicate / column-ref evaluation. Every column is present
 * (null cells → `missing`), so a predicate over sparse rows evaluates rather
 * than throwing UndefinedVariable.
 */
function rowScope(row, tableValue) {
    const scope = new Map();
    if (row.index !== undefined && row.index !== null && tableValue.indexColumn) {
        const axis = indexToEvalValue(row.index, tableValue.schema.indexColumn?.type ?? "number");
        scope.set("_index", axis);
        // A predicate can name the axis by its column: where(t, organism == "TC").
        scope.set(tableValue.indexColumn, axis);
    }
    for (const column of tableValue.schema.columns) {
        const cell = row.data[column.name];
        scope.set(column.name, cell === null || cell === undefined
            ? MISSING_VALUE
            : cellToEvalValue(cell, column));
    }
    return scope;
}
/** Extract the raw AST from a deferred (lazy) argument. */
function deferredAst(arg, context, fnName) {
    if (arg.type !== "deferred") {
        return context.createError("InvalidArgumentType", `Function "${fnName}" expects an inline expression argument.`);
    }
    return arg.value;
}
/** Evaluate a predicate against a row. true/false, or EvalError to propagate. */
function testPredicate(ast, row, tableValue, context, fnName) {
    const result = context.evaluateInRowScope(ast, rowScope(row, tableValue));
    if (isEvalError(result))
        return result;
    if (result.type === "missing")
        return false; // missing operand → no match
    if (result.type !== "boolean") {
        return context.createError("InvalidCondition", `Function "${fnName}" expects a condition (e.g. \`temp > 30\`), but the argument is not a boolean.`);
    }
    return Boolean(result.value);
}
/** Evaluate a per-row numeric expression. number, null (skip), or EvalError. */
function evalRowNumber(ast, row, tableValue, context, fnName) {
    const result = context.evaluateInRowScope(ast, rowScope(row, tableValue));
    if (isEvalError(result))
        return result;
    if (result.type === "missing")
        return null; // skip rows with no value
    if (result.type !== "scalar" || typeof result.value !== "number") {
        return context.createError("NonNumericColumn", `Function "${fnName}" expects a numeric column/expression, but the argument is type "${result.type}".`);
    }
    if (isNaN(result.value))
        return null;
    return result.value;
}
// ---------------------------------------------------------------------------
// Get — retrieve a specific row by index value
// ---------------------------------------------------------------------------
export class GetFunction extends TableFunction {
    signature = {
        name: "get",
        parameters: [
            { name: "table", type: "table", description: "Source table" },
            {
                name: "index_value",
                type: ["scalar", "text"],
                description: "Index value to find",
            },
        ],
        returnType: "object",
        description: "Retrieve a specific row by index value",
        examples: ["get(measurements, 1704067200).temperature"],
        category: "table",
    };
    processTable(table, args, context) {
        if (args.length !== 1) {
            return context.createError("WrongNumberOfArguments", `Function "get" expects 2 arguments (table, index_value), but received ${args.length + 1}.`);
        }
        const indexValue = args[0];
        if (indexValue.type !== "scalar" && indexValue.type !== "text") {
            return context.createError("InvalidArgumentType", `Function "get" expects a number or text index value, but received type "${indexValue.type}".`);
        }
        const tableValue = table.value;
        if (!tableValue.indexColumn) {
            return context.createError("NoIndexColumn", "Function 'get' requires a table with an index column.");
        }
        const targetRow = tableValue.rows.find((row) => {
            if (row.index === undefined || row.index === null)
                return false;
            if (tableValue.schema.indexColumn?.type === "timestamp") {
                const rowTime = new Date(row.index).getTime() / 1000;
                return Math.abs(rowTime - indexValue.value) < 1; // 1s tolerance
            }
            else if (tableValue.schema.indexColumn?.type === "number") {
                return Number(row.index) === indexValue.value;
            }
            else {
                return textEquals(String(row.index), String(indexValue.value));
            }
        });
        if (!targetRow) {
            return context.createError("IndexNotFound", `Index value "${indexValue.value}" not found in table.`);
        }
        return buildRowObject(targetRow, tableValue);
    }
}
// ---------------------------------------------------------------------------
// Where — filter rows by a predicate (returns a table)
// ---------------------------------------------------------------------------
export class WhereFunction extends TableFunction {
    signature = {
        name: "where",
        parameters: [
            { name: "table", type: "table", description: "Source table" },
            {
                name: "condition",
                type: "predicate",
                lazy: true,
                description: "Row condition, e.g. `temp > 30 and ph < 7`",
            },
        ],
        returnType: "table",
        description: "Filter rows to those matching a condition",
        examples: ["last(where(measurements, temp > 30)).ph"],
        category: "table",
    };
    processTable(table, args, context) {
        const ast = deferredAst(args[0], context, "where");
        if (isEvalError(ast))
            return ast;
        const tableValue = table.value;
        const filteredRows = [];
        for (const row of tableValue.rows) {
            const match = testPredicate(ast, row, tableValue, context, "where");
            if (typeof match !== "boolean")
                return match; // EvalError
            if (match)
                filteredRows.push(row);
        }
        return {
            type: "table",
            value: {
                schema: tableValue.schema,
                rows: filteredRows,
                indexColumn: tableValue.indexColumn,
                metadata: tableValue.metadata,
            },
        };
    }
}
// ---------------------------------------------------------------------------
// Find / FindLast — first/last row matching a predicate (returns a row object)
// ---------------------------------------------------------------------------
export class FindFunction extends TableFunction {
    signature = {
        name: "find",
        parameters: [
            { name: "table", type: "table", description: "Source table" },
            {
                name: "condition",
                type: "predicate",
                lazy: true,
                description: "Row condition, e.g. `temp > 30`",
            },
        ],
        returnType: "object",
        description: "Find the first row matching a condition",
        examples: ["find(measurements, temp > 30).ph"],
        category: "table",
    };
    processTable(table, args, context) {
        const ast = deferredAst(args[0], context, "find");
        if (isEvalError(ast))
            return ast;
        const tableValue = table.value;
        for (const row of tableValue.rows) {
            const match = testPredicate(ast, row, tableValue, context, "find");
            if (typeof match !== "boolean")
                return match;
            if (match)
                return buildRowObject(row, tableValue);
        }
        return context.createError("RowNotFound", "No row found matching the specified condition.");
    }
}
export class FindLastFunction extends TableFunction {
    signature = {
        name: "findlast",
        parameters: [
            { name: "table", type: "table", description: "Source table" },
            {
                name: "condition",
                type: "predicate",
                lazy: true,
                description: "Row condition, e.g. `temp > 30`",
            },
        ],
        returnType: "object",
        description: "Find the last row matching a condition",
        examples: ["findlast(measurements, temp > 30).ph"],
        category: "table",
    };
    processTable(table, args, context) {
        const ast = deferredAst(args[0], context, "findlast");
        if (isEvalError(ast))
            return ast;
        const tableValue = table.value;
        for (let i = tableValue.rows.length - 1; i >= 0; i--) {
            const row = tableValue.rows[i];
            const match = testPredicate(ast, row, tableValue, context, "findlast");
            if (typeof match !== "boolean")
                return match;
            if (match)
                return buildRowObject(row, tableValue);
        }
        return context.createError("RowNotFound", "No row found matching the specified condition.");
    }
}
// ---------------------------------------------------------------------------
// First / Last / Nth — positional row access (returns a row object)
// ---------------------------------------------------------------------------
export class FirstFunction extends TableFunction {
    signature = {
        name: "first",
        parameters: [{ name: "table", type: "table", description: "Source table" }],
        returnType: "object",
        description: "Get the first row from a table",
        examples: ["first(measurements).temperature"],
        category: "table",
    };
    processTable(table, args, context) {
        if (args.length !== 0) {
            return context.createError("WrongNumberOfArguments", `Function "first" expects 1 argument (table), but received ${args.length + 1}.`);
        }
        const tableValue = table.value;
        if (tableValue.rows.length === 0) {
            return context.createError("EmptyTable", "Cannot get first row from empty table.");
        }
        return buildRowObject(tableValue.rows[0], tableValue);
    }
}
export class LastFunction extends TableFunction {
    signature = {
        name: "last",
        parameters: [{ name: "table", type: "table", description: "Source table" }],
        returnType: "object",
        description: "Get the last row from a table",
        examples: ["last(measurements).temperature"],
        category: "table",
    };
    processTable(table, args, context) {
        if (args.length !== 0) {
            return context.createError("WrongNumberOfArguments", `Function "last" expects 1 argument (table), but received ${args.length + 1}.`);
        }
        const tableValue = table.value;
        if (tableValue.rows.length === 0) {
            return context.createError("EmptyTable", "Cannot get last row from empty table.");
        }
        return buildRowObject(tableValue.rows[tableValue.rows.length - 1], tableValue);
    }
}
export class NthFunction extends TableFunction {
    signature = {
        name: "nth",
        parameters: [
            { name: "table", type: "table", description: "Source table" },
            { name: "index", type: "scalar", description: "Zero-based row index" },
        ],
        returnType: "object",
        description: "Get the nth row (0-indexed) from a table",
        examples: ["nth(measurements, 5).temperature"],
        category: "table",
    };
    processTable(table, args, context) {
        if (args.length !== 1) {
            return context.createError("WrongNumberOfArguments", `Function "nth" expects 2 arguments (table, index), but received ${args.length + 1}.`);
        }
        const indexArg = args[0];
        if (indexArg.type !== "scalar") {
            return context.createError("InvalidArgumentType", `Function "nth" expects scalar index, but received type "${indexArg.type}".`);
        }
        const tableValue = table.value;
        const index = Math.floor(indexArg.value);
        if (index < 0 || index >= tableValue.rows.length) {
            return context.createError("IndexOutOfBounds", `Row index ${index} is out of bounds. Table has ${tableValue.rows.length} rows.`);
        }
        return buildRowObject(tableValue.rows[index], tableValue);
    }
}
// ---------------------------------------------------------------------------
// MaxRow / MinRow / MedianRow — pick a row by a per-row numeric expression
// ---------------------------------------------------------------------------
class ExtremeRowFunction extends TableFunction {
    processTable(table, args, context) {
        const ast = deferredAst(args[0], context, this.fnName);
        if (isEvalError(ast))
            return ast;
        const tableValue = table.value;
        if (tableValue.rows.length === 0) {
            return context.createError("EmptyTable", `Cannot evaluate ${this.fnName} on an empty table.`);
        }
        const valued = [];
        for (const row of tableValue.rows) {
            const v = evalRowNumber(ast, row, tableValue, context, this.fnName);
            if (isEvalError(v))
                return v;
            if (v === null)
                continue; // missing — skip
            valued.push({ row, value: v });
        }
        if (valued.length === 0) {
            return context.createError("NoValidData", `No valid numeric values found for ${this.fnName}.`);
        }
        const chosen = this.pick(valued);
        return buildRowObject(chosen.row, tableValue);
    }
}
export class MaxRowFunction extends ExtremeRowFunction {
    fnName = "maxrow";
    signature = {
        name: "maxrow",
        parameters: [
            { name: "table", type: "table", description: "Source table" },
            {
                name: "column",
                type: "column_ref",
                lazy: true,
                description: "Numeric column/expression to maximise, e.g. `od`",
            },
        ],
        returnType: "object",
        description: "Get the row with the maximum value of a column/expression",
        examples: ["maxrow(measurements, od).ph"],
        category: "table",
    };
    pick(valued) {
        return valued.reduce((a, b) => (b.value > a.value ? b : a));
    }
}
export class MinRowFunction extends ExtremeRowFunction {
    fnName = "minrow";
    signature = {
        name: "minrow",
        parameters: [
            { name: "table", type: "table", description: "Source table" },
            {
                name: "column",
                type: "column_ref",
                lazy: true,
                description: "Numeric column/expression to minimise, e.g. `ph`",
            },
        ],
        returnType: "object",
        description: "Get the row with the minimum value of a column/expression",
        examples: ["minrow(measurements, ph).temperature"],
        category: "table",
    };
    pick(valued) {
        return valued.reduce((a, b) => (b.value < a.value ? b : a));
    }
}
export class MedianRowFunction extends ExtremeRowFunction {
    fnName = "medianrow";
    signature = {
        name: "medianrow",
        parameters: [
            { name: "table", type: "table", description: "Source table" },
            {
                name: "column",
                type: "column_ref",
                lazy: true,
                description: "Numeric column/expression for the median, e.g. `od`",
            },
        ],
        returnType: "object",
        description: "Get the row whose value is closest to the median of a column/expression",
        examples: ["medianrow(measurements, od).temperature"],
        category: "table",
    };
    pick(valued) {
        const sorted = [...valued].sort((a, b) => a.value - b.value);
        const mid = Math.floor(sorted.length / 2);
        const median = sorted.length % 2 === 0
            ? (sorted[mid - 1].value + sorted[mid].value) / 2
            : sorted[mid].value;
        return valued.reduce((a, b) => Math.abs(b.value - median) < Math.abs(a.value - median) ? b : a);
    }
}
function parseFrequency(freq) {
    const match = freq.match(/^(\d+)(min|h|d|w|M)$/);
    if (!match)
        return null;
    return {
        amount: parseInt(match[1]),
        unit: match[2],
    };
}
function frequencyToMilliseconds(freq) {
    const multipliers = {
        min: 60 * 1000,
        h: 60 * 60 * 1000,
        d: 24 * 60 * 60 * 1000,
        w: 7 * 24 * 60 * 60 * 1000,
        M: 30 * 24 * 60 * 60 * 1000, // Approximate month
    };
    return freq.amount * multipliers[freq.unit];
}
export class ResampleFunction extends TableFunction {
    signature = {
        name: "resample",
        parameters: [
            {
                name: "table",
                type: "table",
                description: "Source table with timestamp index",
            },
            {
                name: "frequency",
                type: "scalar",
                description: "Time frequency (e.g., '1h', '30min', '1d')",
            },
            {
                name: "aggregation",
                type: "scalar",
                description: "Aggregation method ('mean', 'sum', 'min', 'max', 'count', 'first', 'last')",
            },
        ],
        returnType: "table",
        description: "Resample time-indexed data to a different frequency with aggregation",
        examples: [
            "resample(Measurements, '1h', 'mean')",
            "resample(Measurements, '15min', 'max')",
        ],
        category: "table",
    };
    processTable(table, args, context) {
        if (args.length !== 2) {
            return context.createError("WrongNumberOfArguments", `Function "resample" expects 3 arguments (table, frequency, aggregation), but received ${args.length + 1}.`);
        }
        const frequencyArg = args[0];
        const aggregationArg = args[1];
        if (frequencyArg.type !== "scalar" || aggregationArg.type !== "scalar") {
            return context.createError("InvalidArgumentType", "Function 'resample' expects string frequency and aggregation arguments.");
        }
        const tableValue = table.value;
        const frequencyStr = String(frequencyArg.value);
        const aggregationStr = String(aggregationArg.value);
        if (!tableValue.indexColumn ||
            tableValue.schema.indexColumn?.type !== "timestamp") {
            return context.createError("NonTimestampIndex", "Function 'resample' requires a table with a timestamp index column.");
        }
        const frequency = parseFrequency(frequencyStr);
        if (!frequency) {
            return context.createError("InvalidFrequency", `Invalid frequency format: "${frequencyStr}". Expected format like "1h", "30min", "1d".`);
        }
        const validAggregations = [
            "mean",
            "sum",
            "min",
            "max",
            "count",
            "first",
            "last",
        ];
        if (!validAggregations.includes(aggregationStr)) {
            return context.createError("InvalidAggregation", `Invalid aggregation method: "${aggregationStr}". Valid methods: ${validAggregations.join(", ")}.`);
        }
        const sortedRows = [...tableValue.rows].sort((a, b) => {
            const timeA = new Date(a.index).getTime();
            const timeB = new Date(b.index).getTime();
            return timeA - timeB;
        });
        if (sortedRows.length === 0) {
            return {
                type: "table",
                value: {
                    schema: tableValue.schema,
                    rows: [],
                    indexColumn: tableValue.indexColumn,
                    metadata: tableValue.metadata,
                },
            };
        }
        const frequencyMs = frequencyToMilliseconds(frequency);
        const startTime = new Date(sortedRows[0].index).getTime();
        const endTime = new Date(sortedRows[sortedRows.length - 1].index).getTime();
        const resampledRows = [];
        for (let bucketStart = startTime; bucketStart <= endTime; bucketStart += frequencyMs) {
            const bucketEnd = bucketStart + frequencyMs;
            const bucketRows = sortedRows.filter((row) => {
                const rowTime = new Date(row.index).getTime();
                return rowTime >= bucketStart && rowTime < bucketEnd;
            });
            if (bucketRows.length > 0) {
                const aggregatedData = {};
                for (const column of tableValue.schema.columns) {
                    if (column.type === "number") {
                        const values = bucketRows
                            .map((row) => Number(row.data[column.name]))
                            .filter((val) => !isNaN(val));
                        if (values.length > 0) {
                            switch (aggregationStr) {
                                case "mean":
                                    aggregatedData[column.name] =
                                        values.reduce((a, b) => a + b, 0) / values.length;
                                    break;
                                case "sum":
                                    aggregatedData[column.name] = values.reduce((a, b) => a + b, 0);
                                    break;
                                case "min":
                                    aggregatedData[column.name] = Math.min(...values);
                                    break;
                                case "max":
                                    aggregatedData[column.name] = Math.max(...values);
                                    break;
                                case "count":
                                    aggregatedData[column.name] = values.length;
                                    break;
                                case "first":
                                    aggregatedData[column.name] = values[0];
                                    break;
                                case "last":
                                    aggregatedData[column.name] = values[values.length - 1];
                                    break;
                            }
                        }
                    }
                    else {
                        const nonNullValues = bucketRows
                            .map((row) => row.data[column.name])
                            .filter((val) => val !== null && val !== undefined);
                        if (nonNullValues.length > 0) {
                            aggregatedData[column.name] = nonNullValues[0];
                        }
                    }
                }
                resampledRows.push({
                    index: new Date(bucketStart),
                    data: aggregatedData,
                });
            }
        }
        return {
            type: "table",
            value: {
                schema: tableValue.schema,
                rows: resampledRows,
                indexColumn: tableValue.indexColumn,
                metadata: tableValue.metadata,
            },
        };
    }
}
//# sourceMappingURL=table-functions.js.map